/**
 * mk-cart-delivery-resync.js
 *
 * Holder line item properties Leveringstid / Forventet på lager / _in_stock_ synkroniseret
 * med produkt-feedet. Snapshottet ved add-to-cart kan blive forældet hvis lagerstatus
 * ændrer sig efter varen er lagt i kurv.
 *
 * Hooks:
 *   window.mkCartDeliveryResync.run()   — kører resync, returnerer Promise
 *   window.mkCartDeliveryResync.debug   — true/false, toggler verbose logging
 */
(function () {
  'use strict';

  // Legacy danske keys + nuværende lokaliserede labels skal alle ryddes op før
  // den nye property tilføjes. Uden legacy-fjernelse ville items lagt i kurv før
  // locale-fixet beholde "Leveringstid" på DE og duplikere row sammen med ny "Lieferzeit".
  var LEGACY_PROPS = ['Leveringstid', 'Forventet på lager', 'Status'];
  function buildDeliveryProps() {
    var labels = (typeof window !== 'undefined' && window.mkDeliveryLabels) || {};
    var localized = [labels.deliveryTime, labels.expectedDate, labels.status].filter(Boolean);
    var combined = LEGACY_PROPS.slice();
    localized.forEach(function (name) {
      if (combined.indexOf(name) === -1) combined.push(name);
    });
    return combined;
  }
  var DELIVERY_PROPS = buildDeliveryProps();
  var IN_STOCK_PROP = '_in_stock_';
  // Letvægts settings-fri sektion der kun udskriver real-time lager-data som JSON.
  // (main-sektionen kan ikke renderes via Section Rendering API — for stor → null.)
  var SECTION_ID = 'mk-stock-data';
  var TAG = '[mk-resync]';

  var inFlight = null;
  var debugMode = false;
  var lastUserQtyChangeAt = 0;
  // Leveringstid-ændring-modal: ændringer (ATC-baseline vs live) opsamlet i det aktuelle run.
  var runChanges = [];

  function log() {
    if (!debugMode) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.log.apply(console, args);
  }
  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.warn.apply(console, args);
  }
  function group(label) {
    if (!debugMode) return;
    console.group(TAG + ' ' + label);
  }
  function groupEnd() {
    if (!debugMode) return;
    console.groupEnd();
  }

  function fetchCart() {
    log('GET /cart.js');
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart.js fetch failed: ' + r.status);
      return r.json();
    });
  }

  function fetchStockData(handle, variantId) {
    // Henter den letvægts mk-stock-data-sektion og parser dens JSON (real-time lager).
    var url = '/products/' + handle + '?variant=' + variantId + '&sections=' + SECTION_ID + '&_t=' + Date.now();
    log('GET', url);
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('sections fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (sections) {
        var html = sections && sections[SECTION_ID];
        if (!html || typeof html !== 'string') {
          throw new Error('sections response missing "' + SECTION_ID + '" key — got: ' + Object.keys(sections || {}).join(','));
        }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var dataEl = doc.querySelector('#mk-stock-data');
        if (!dataEl) throw new Error('#mk-stock-data not found in section');
        var data = JSON.parse(dataEl.textContent || '{}');
        log('  ↳ stock data:', data);
        return data;
      });
  }

  // Vælger leveringstid ud fra real-time lager + snapshot-værdier på linjen.
  // Status (in-stock vs udsolgt/oversold) beregnes LIVE; kun værdierne er snapshot.
  function resolveDeliveryInfo(props, stock, quantity) {
    var inv = parseInt(stock && stock.inventory_quantity, 10);
    if (isNaN(inv)) inv = 0;
    var inStock = inv > 0 && inv >= quantity;
    if (inStock) {
      return {
        property: props['_dlv_instock_prop_'] || '',
        value: props['_dlv_instock_'] || '',
        show: true,
        inStock: true
      };
    }
    return {
      property: props['_dlv_order_prop_'] || props['_dlv_instock_prop_'] || '',
      value: props['_dlv_order_'] || '',
      show: true,
      inStock: false
    };
  }

  function buildNewProperties(currentProps, deliveryInfo) {
    // Safety: hvis endpoint ikke gav valid shape, returner currentProps uændret
    if (!deliveryInfo || typeof deliveryInfo.show === 'undefined') {
      return currentProps || {};
    }

    var next = {};
    if (currentProps && typeof currentProps === 'object') {
      Object.keys(currentProps).forEach(function (k) {
        if (DELIVERY_PROPS.indexOf(k) === -1 && k !== IN_STOCK_PROP) {
          next[k] = currentProps[k];
        }
      });
    }

    if (deliveryInfo.show && deliveryInfo.property && deliveryInfo.value) {
      next[deliveryInfo.property] = deliveryInfo.value;
    }
    next[IN_STOCK_PROP] = deliveryInfo.inStock ? 'true' : 'false';

    return next;
  }

  function propsAreEqual(a, b) {
    var ak = Object.keys(a || {});
    var bk = Object.keys(b || {});
    if (ak.length !== bk.length) return false;
    for (var i = 0; i < ak.length; i++) {
      if (String(a[ak[i]]) !== String(b[ak[i]])) return false;
    }
    return true;
  }

  function changeLineItem(lineKey, newProperties, quantity) {
    // quantity SKAL sendes med — ellers nulstiller /cart/change.js antallet (til 1)
    // når properties ændres, fordi linjens identitet (variant+properties) ændrer sig.
    var payload = { id: lineKey, properties: newProperties };
    if (typeof quantity === 'number' && quantity > 0) payload.quantity = quantity;
    log('POST /cart/change.js', payload);
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        log('  ↳ /cart/change.js status:', r.status);
        if (!r.ok) throw new Error('cart/change failed: ' + r.status);
        return r.json();
      })
      .then(function (result) {
        var hit = result.items && result.items.find(function (it) { return it.key === lineKey; });
        log('  ↳ updated line props:', hit ? hit.properties : '(not found in response)');
        return result;
      });
  }

  function syncItem(item) {
    if (!item || !item.handle || !item.variant_id) {
      warn('skip item, missing handle/variant_id:', item);
      return Promise.resolve(false);
    }

    var props = item.properties || {};
    // Kræver snapshot-properties (sat af graphie-stock-block ved add-to-cart).
    // Mangler de (vare lagt i kurv før denne feature / fra ikke-PDP-kilde) → spring over.
    if (!props['_dlv_instock_'] && !props['_dlv_order_']) {
      log('ingen snapshot-properties på linjen — spring over');
      return Promise.resolve(false);
    }

    group('item: ' + item.product_title + ' (' + item.handle + ')');
    log('current properties:', props);
    log('variant_id:', item.variant_id, '| key:', item.key, '| qty:', item.quantity);

    return fetchStockData(item.handle, item.variant_id)
      .then(function (stock) {
        var info = resolveDeliveryInfo(props, stock, item.quantity);
        log('  ↳ resolved (live status):', info);
        detectDeliveryChange(item, props, info);
        var nextProps = buildNewProperties(props, info);
        log('proposed nextProps:', nextProps);
        var equal = propsAreEqual(props, nextProps);
        log('propsAreEqual:', equal);
        if (equal) {
          log('→ NO CHANGE');
          groupEnd();
          return false;
        }
        log('→ WILL UPDATE via /cart/change.js');
        return changeLineItem(item.key, nextProps, item.quantity).then(function () {
          groupEnd();
          return true;
        });
      })
      .catch(function (err) {
        warn('skip', item.handle, err.message);
        groupEnd();
        return false;
      });
  }

  function inspectThemeCart() {
    var theme = window.MinimogTheme && window.MinimogTheme.Cart;
    if (!theme) {
      warn('window.MinimogTheme.Cart not found — drawer DOM cannot be re-rendered');
      return null;
    }
    log('MinimogTheme.Cart present. Methods:', {
      refreshCart: typeof theme.refreshCart,
      renderNewCart: typeof theme.renderNewCart,
      updateCartCount: typeof theme.updateCartCount
    });
    return theme;
  }

  // Henter frisk cart-drawer-sektion og erstatter .scd__body/.scd__summary direkte.
  // Mere robust end temaets renderNewCart() (som kan tabe race'en mod temaets egen
  // antal-render og efterlade den åbne drawer med gammel leveringstid).
  function directDrawerRender(theme) {
    if (!theme || typeof theme.fetchCartSection !== 'function') return Promise.resolve(false);
    return theme.fetchCartSection().then(function (cartHTML) {
      if (!cartHTML) return false;
      var newBody = cartHTML.querySelector('.scd__body');
      var newSummary = cartHTML.querySelector('.scd__summary');
      var curBody = document.querySelector('.scd__body');
      var curSummary = document.querySelector('.scd__summary');
      var fadeIns = [];
      if (fadeDeliveryNextRender && newBody) {
        fadeIns = deliveryValueSpans(newBody);
        fadeIns.forEach(function (el) {
          el.classList.add('mk-delivery-fade');
          el.style.opacity = '0';
        });
        fadeDeliveryNextRender = false;
      }
      var did = false;
      if (newBody && curBody) { curBody.replaceWith(newBody); did = true; }
      if (newSummary && curSummary) { curSummary.replaceWith(newSummary); did = true; }
      if (theme.selectors) { try { theme.domNodes = (typeof queryDomNodes === 'function') ? queryDomNodes(theme.selectors) : theme.domNodes; } catch (e) {} }
      if (fadeIns.length) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            fadeIns.forEach(function (el) { el.style.opacity = '1'; });
          });
        });
      }
      log('  ↳ directDrawerRender replaced body/summary:', did);
      return did;
    });
  }

  function refreshThemeCartUI(updatedCart) {
    group('refreshThemeCartUI');
    var theme = inspectThemeCart();
    return Promise.resolve()
      .then(function () {
        if (theme && typeof theme.refreshCart === 'function') {
          log('→ calling refreshCart()');
          return theme.refreshCart();
        }
      })
      .then(function () {
        // Lille delay så temaets egen antal-render er færdig først → vi renderer sidst.
        return new Promise(function (resolve) { setTimeout(resolve, 300); });
      })
      .then(function () {
        log('→ directDrawerRender()');
        return directDrawerRender(theme);
      })
      .then(function () {
        var cartForUpdate = (theme && theme.cart) || updatedCart;
        if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
          window.Shopify.onCartUpdate(cartForUpdate, false);
        }
        if (theme && typeof theme.updateCartCount === 'function') {
          theme.updateCartCount(cartForUpdate);
        }
        log('refresh done');
        groupEnd();
      })
      .catch(function (e) {
        warn('refreshThemeCartUI failed', e);
        groupEnd();
      });
  }

  var deliveryLabelCache = null;
  function deliveryLabelSet() {
    if (deliveryLabelCache) return deliveryLabelCache;
    var l = (typeof window !== 'undefined' && window.mkDeliveryLabels) || {};
    var set = {};
    [l.deliveryTime, l.expectedDate, l.status, l.remoteStock].concat(LEGACY_PROPS).forEach(function (name) {
      if (name) set[String(name).trim().toLowerCase()] = true;
    });
    deliveryLabelCache = set;
    return set;
  }

  function cartDrawerIsOpen() {
    var w = document.querySelector('.scd__wrapper');
    return !!(w && !w.classList.contains('hidden'));
  }

  function deliveryValueSpans(root) {
    var out = [];
    var labels = deliveryLabelSet();
    (root || document).querySelectorAll('.scd-item__properties p').forEach(function (p) {
      var labelEl = p.querySelector('.font-medium');
      if (!labelEl) return;
      var key = labelEl.textContent.replace(/:\s*$/, '').trim().toLowerCase();
      if (!labels[key]) return;
      var valueEl = labelEl.nextElementSibling;
      if (valueEl) out.push(valueEl);
    });
    return out;
  }

  function ensureLoaderStyles() {
    if (document.getElementById('mk-delivery-loading-style')) return;
    var s = document.createElement('style');
    s.id = 'mk-delivery-loading-style';
    s.textContent = '.mk-delivery-fade{transition:opacity .2s ease}.mk-delivery-loading{animation:mkDeliveryPulse 2.4s ease-in-out infinite}@keyframes mkDeliveryPulse{0%,100%{opacity:.25}50%{opacity:.5}}';
    document.head.appendChild(s);
  }

  var loaderRefs = [];
  var fadeDeliveryNextRender = false;
  function showDeliveryLoading() {
    ensureLoaderStyles();
    var labels = deliveryLabelSet();
    var text = (window.mkDeliveryLabels && window.mkDeliveryLabels.updating) || 'Opdaterer leveringstid…';
    var shown = false;
    document.querySelectorAll('.scd-item__properties p').forEach(function (p) {
      var labelEl = p.querySelector('.font-medium');
      if (!labelEl) return;
      var key = labelEl.textContent.replace(/:\s*$/, '').trim().toLowerCase();
      if (!labels[key]) return;
      var valueEl = labelEl.nextElementSibling;
      if (!valueEl) return;
      loaderRefs.push({ el: valueEl, html: valueEl.innerHTML });
      valueEl.classList.add('mk-delivery-fade');
      valueEl.setAttribute('aria-busy', 'true');
      valueEl.setAttribute('aria-label', text);
      valueEl.style.opacity = '0';
      (function (el) {
        setTimeout(function () {
          el.textContent = text;
          el.style.opacity = '';
          el.classList.add('mk-delivery-loading');
        }, 180);
      })(valueEl);
      shown = true;
    });
    if (shown) fadeDeliveryNextRender = true;
    return shown;
  }

  function clearDeliveryLoading() {
    loaderRefs.forEach(function (ref) {
      var el = ref.el;
      if (!el) return;
      el.classList.remove('mk-delivery-loading');
      el.classList.add('mk-delivery-fade');
      el.style.opacity = '0';
      (function (node, html) {
        setTimeout(function () {
          node.innerHTML = html;
          node.removeAttribute('aria-busy');
          node.removeAttribute('aria-label');
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { node.style.opacity = '1'; });
          });
        }, 180);
      })(el, ref.html);
    });
    loaderRefs = [];
    fadeDeliveryNextRender = false;
  }

  // Rekonstruerer den leveringstid der blev vist ved add-to-cart ud fra de urørte
  // snapshot-properties (_avail_ + _dlv_instock_/_dlv_order_). Resync overskriver
  // aldrig disse, så vi har altid ATC-baselinen — også efter en tidligere resync.
  function originalDeliveryValue(props) {
    var avail = parseInt(props && props['_avail_'], 10);
    var inStockAtAdd = !isNaN(avail) && avail > 0;
    return inStockAtAdd ? (props['_dlv_instock_'] || '') : (props['_dlv_order_'] || '');
  }

  // Kvitterings-dedup: naar kunden har SET modalen for en aendring (variant + ny
  // vaerdi), gemmes den i sessionStorage, saa samme aendring ikke blokerer checkout igen.
  function changeDedupKey(item, newValue) {
    return 'mk-dlv-chg:' + item.variant_id + ':' + newValue;
  }
  function changeAcknowledged(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }
  function markChangeAcknowledged(key) {
    try { sessionStorage.setItem(key, '1'); } catch (e) {}
  }

  // Sammenligner ATC-baselinen (det kunden saa da varen kom i kurven) med den
  // live-beregnede vaerdi. Aendringer opsamles altid — men vises kun ved checkout,
  // og kun hvis kunden ikke allerede har kvitteret for praecis denne aendring.
  function detectDeliveryChange(item, props, info) {
    if (!info || !info.value) return;
    var original = originalDeliveryValue(props);
    if (!original) return;
    var current = String(info.value);
    if (String(original).trim() === current.trim()) return;
    var key = changeDedupKey(item, current);
    if (changeAcknowledged(key)) return;
    for (var i = 0; i < runChanges.length; i++) {
      if (runChanges[i].key === key) return;
    }
    runChanges.push({
      title: item.product_title || item.title || '',
      original: String(original),
      current: current,
      key: key
    });
    log('  ↳ delivery CHANGED since ATC:', original, '→', current);
  }

  function ensureModalStyles() {
    if (document.getElementById('mk-delivery-modal-style')) return;
    var s = document.createElement('style');
    s.id = 'mk-delivery-modal-style';
    s.textContent =
      '.mk-dlv-modal{position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:20px;opacity:0;transition:opacity .2s ease}' +
      '.mk-dlv-modal.is-open{opacity:1}' +
      '.mk-dlv-modal__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.45)}' +
      '.mk-dlv-modal__panel{position:relative;background:#fff;max-width:440px;width:100%;padding:28px 26px;box-shadow:0 20px 60px rgba(0,0,0,.25);transform:translateY(8px);transition:transform .2s ease}' +
      '.mk-dlv-modal.is-open .mk-dlv-modal__panel{transform:none}' +
      '.mk-dlv-modal__title{font-size:19px;font-weight:600;margin:0 0 10px;color:#1a1a1a}' +
      '.mk-dlv-modal__intro{font-size:14px;line-height:1.45;color:#4b5563;margin:0 0 16px}' +
      '.mk-dlv-modal__list{list-style:none;margin:0 0 22px;padding:0;display:flex;flex-direction:column;gap:12px}' +
      '.mk-dlv-modal__item{border-left:2px solid #d9d4cb;padding-left:12px}' +
      '.mk-dlv-modal__name{display:block;font-size:14px;font-weight:600;color:#1a1a1a;margin-bottom:2px}' +
      '.mk-dlv-modal__times{font-size:13px;color:#4b5563}' +
      '.mk-dlv-modal__old{text-decoration:line-through;opacity:.6;margin-right:6px}' +
      '.mk-dlv-modal__new{font-weight:600;color:#1a1a1a}' +
      '.mk-dlv-modal__panel .sf__btn{width:100%;justify-content:center}' +
      '.mk-dlv-modal__close{position:absolute;top:14px;right:14px;background:none;border:0;cursor:pointer;line-height:0;color:#9ca3af;padding:4px}';
    document.head.appendChild(s);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function showDeliveryChangeModal(changes, onContinue) {
    if (!changes || !changes.length) return;
    ensureModalStyles();
    var labels = (window.mkDeliveryLabels || {});
    var title = labels.changeTitle || 'Leveringstiden er ændret';
    var intro = labels.changeIntro || 'Leveringstiden på følgende har ændret sig, siden du lagde det i kurven:';
    var btnLabel = labels.changeButton || 'Fortsæt til kassen';

    var existing = document.querySelector('.mk-dlv-modal');
    if (existing) existing.parentNode.removeChild(existing);

    var itemsHtml = changes.map(function (c) {
      return '<li class="mk-dlv-modal__item">' +
        '<span class="mk-dlv-modal__name">' + escapeHtml(c.title) + '</span>' +
        '<span class="mk-dlv-modal__times">' +
          '<span class="mk-dlv-modal__old">' + escapeHtml(c.original) + '</span>' +
          '<span class="mk-dlv-modal__new">' + escapeHtml(c.current) + '</span>' +
        '</span></li>';
    }).join('');

    var overlay = document.createElement('div');
    overlay.className = 'mk-dlv-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', title);
    overlay.innerHTML =
      '<div class="mk-dlv-modal__backdrop" data-mk-dlv-close></div>' +
      '<div class="mk-dlv-modal__panel">' +
        '<button type="button" class="mk-dlv-modal__close" data-mk-dlv-close aria-label="Luk">' +
          '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6"><line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/></svg>' +
        '</button>' +
        '<p class="mk-dlv-modal__title">' + escapeHtml(title) + '</p>' +
        '<p class="mk-dlv-modal__intro">' + escapeHtml(intro) + '</p>' +
        '<ul class="mk-dlv-modal__list">' + itemsHtml + '</ul>' +
        '<button type="button" class="sf__btn sf__btn-primary" data-mk-dlv-continue>' + escapeHtml(btnLabel) + '</button>' +
      '</div>';

    // Uanset hvordan modalen lukkes, har kunden SET aendringen → kvitter, saa
    // naeste checkout-klik gaar rent igennem.
    function acknowledgeAll() {
      changes.forEach(function (c) { if (c.key) markChangeAcknowledged(c.key); });
    }

    function close() {
      overlay.classList.remove('is-open');
      document.removeEventListener('keydown', onKey, true);
      setTimeout(function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }, 220);
    }
    function onKey(e) { if (e.key === 'Escape') { acknowledgeAll(); close(); } }

    overlay.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-mk-dlv-continue]')) {
        acknowledgeAll();
        close();
        if (typeof onContinue === 'function') onContinue();
        return;
      }
      if (e.target.closest && e.target.closest('[data-mk-dlv-close]')) {
        acknowledgeAll();
        close();
      }
    });
    document.addEventListener('keydown', onKey, true);

    document.body.appendChild(overlay);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        var btn = overlay.querySelector('[data-mk-dlv-continue]');
        if (btn) { try { btn.focus(); } catch (e) {} }
      });
    });
  }

  function run(triggerLabel) {
    if (inFlight) {
      log('run() requested by [' + (triggerLabel || 'manual') + '] but already in flight');
      return inFlight;
    }

    group('run() — trigger: ' + (triggerLabel || 'manual'));
    var startTime = performance.now ? performance.now() : Date.now();
    runChanges = [];

    var loaderShown = false;
    if (/ON_CART_UPDATE/.test(triggerLabel || '') && cartDrawerIsOpen() && (Date.now() - lastUserQtyChangeAt) < 1800) {
      loaderShown = showDeliveryLoading();
      log('delivery loading shown:', loaderShown);
    }

    inFlight = fetchCart()
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          log('cart is empty, nothing to sync');
          return false;
        }
        log('cart has', cart.items.length, 'items');
        var work = cart.items.map(function (item) {
          return syncItem(item);
        });
        return Promise.all(work).then(function (results) {
          var changedCount = results.filter(function (c) { return c; }).length;
          log('items changed:', changedCount, '/', results.length);
          return changedCount > 0;
        });
      })
      .then(function (anyChanged) {
        if (!anyChanged) {
          log('no changes — UI refresh skipped');
          return false;
        }
        return fetchCart().then(function (updated) {
          return refreshThemeCartUI(updated).then(function () {
            return true;
          });
        });
      })
      .catch(function (err) {
        warn('run() failed', err);
        return false;
      })
      .then(function (result) {
        if (loaderShown) clearDeliveryLoading();
        var elapsed = (performance.now ? performance.now() : Date.now()) - startTime;
        log('run() done in', Math.round(elapsed), 'ms — anyChanged=', result, '| changes=', runChanges.length);
        groupEnd();
        inFlight = null;
        return result;
      });

    return inFlight;
  }

  window.mkCartDeliveryResync = {
    run: function () { return run('manual'); },
    runAndWait: function () { return run('manual'); },
    get debug() { return debugMode; },
    set debug(v) { debugMode = !!v; },
    inspectThemeCart: inspectThemeCart,
    // Preview af leveringstid-ændring-modalen med dummy-data (til test/QA).
    testModal: function () {
      showDeliveryChangeModal([
        { title: 'Testprodukt', original: '2-5 hverdage', current: '10-16 uger', key: 'mk-dlv-test' }
      ]);
    }
  };

  function attachCartDrawerTriggers() {
    var hooks = [];
    var cartUpdateDebounce = null;

    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-qty-change]')) {
        lastUserQtyChangeAt = Date.now();
      }
    }, true);
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input') && t.closest && t.closest('.scd-item__quantity, [data-cart-item]')) {
        lastUserQtyChangeAt = Date.now();
      }
    }, true);

    // Method 1: MinimogEvents (theme's own event bus — most reliable)
    function tryAttachEventBus() {
      var bus = window.MinimogEvents;
      var subscribe = bus && (bus.subscribe || bus.on);
      if (bus && typeof subscribe === 'function') {
        var on = function (name, cb) { return subscribe.call(bus, name, cb); };
        on('ON_OPEN_DRAWER_CART', function () {
          log('event ON_OPEN_DRAWER_CART → triggering run()');
          run('event:ON_OPEN_DRAWER_CART');
        });
        on('ON_ITEM_ADDED', function () {
          log('event ON_ITEM_ADDED → triggering run()');
          run('event:ON_ITEM_ADDED');
        });
        // Antal-ændringer (+/- i åben drawer/cart) fyrer ON_CART_UPDATE.
        // Debounced så vi lader temaets egen opdatering sætte sig, og så resyncens
        // egen /cart/change.js ikke trigger en uendelig løkke (no-op ved næste run).
        on('ON_CART_UPDATE', function () {
          log('event ON_CART_UPDATE → (debounced) triggering run()');
          if (cartUpdateDebounce) clearTimeout(cartUpdateDebounce);
          cartUpdateDebounce = setTimeout(function () {
            run('event:ON_CART_UPDATE');
          }, 250);
        });
        hooks.push('MinimogEvents');
        return true;
      }
      return false;
    }
    if (!tryAttachEventBus()) {
      log('MinimogEvents not available yet — retrying in 500ms');
      setTimeout(function () {
        if (!tryAttachEventBus()) {
          warn('MinimogEvents still unavailable after retry');
        }
      }, 500);
    }

    // Method 2: MutationObserver on drawer wrapper as fallback
    var drawer = document.querySelector('[data-cart-drawer-backdrop], .scd__wrapper');
    if (drawer) {
      var lastHidden = drawer.classList.contains('hidden');
      log('drawer found. Initial hidden:', lastHidden, '| classes:', drawer.className);
      var observer = new MutationObserver(function (mutations) {
        var nowHidden = drawer.classList.contains('hidden');
        mutations.forEach(function (m) {
          log('drawer mutation:', m.attributeName, '| now hidden:', nowHidden);
        });
        if (lastHidden && !nowHidden) {
          log('drawer transitioned hidden→visible — triggering run()');
          run('mutation-observer');
        }
        lastHidden = nowHidden;
      });
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
      });
      hooks.push('MutationObserver');
    } else {
      warn('cart drawer element (.scd__wrapper) NOT found at attach time');
    }

    // Method 3: click delegation on cart-icon links — last-resort safety net
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-action="open-drawer-cart"], a[href="/cart"], a[href$="/cart"], [data-trigger-cart]');
      if (!trigger) return;
      log('cart-icon click detected on', trigger.tagName, trigger.className, '→ triggering run()');
      run('cart-icon-click');
    }, true);
    hooks.push('click-delegation');

    log('drawer triggers attached:', hooks.join(', '));
  }

  var CHECKOUT_SELECTOR = '.scd__checkout, .cart__submit, [name="checkout"]';
  var checkoutBypassUntil = 0;

  function proceedToCheckout() {
    checkoutBypassUntil = Date.now() + 3000;
    // Frisk opslag — drawer kan vaere re-rendret under resync, saa gamle
    // referencer kan vaere loesrevne DOM-noder (det var derfor re-click fejlede).
    var btn = document.querySelector(CHECKOUT_SELECTOR);
    if (btn && document.contains(btn)) {
      log('proceeding to checkout via fresh button click');
      btn.click();
      return;
    }
    log('no live checkout button found — navigating to /checkout');
    window.location.href = '/checkout';
  }

  function attachCheckoutInterceptor() {
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
        if (!btn) return;
        if (Date.now() < checkoutBypassUntil) {
          log('checkout click within bypass window — letting it through');
          return;
        }

        log('checkout click intercepted — running resync first');
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        btn.classList.add('is-loading');
        run('checkout-click')
          .catch(function (err) {
            warn('checkout resync failed', err);
          })
          .then(function () {
            btn.classList.remove('is-loading');
            if (runChanges.length) {
              // Leveringstid aendret siden ATC og ikke kvitteret → vis modal og HOLD.
              // Primaerknappen fortsaetter selv til kassen efter kvittering.
              log('delivery changed since ATC — showing modal, holding checkout');
              showDeliveryChangeModal(runChanges, proceedToCheckout);
              return;
            }
            proceedToCheckout();
          });
      },
      true
    );
  }

  function init() {
    log('=== mk-cart-delivery-resync.js initialized ===');
    log('debug mode:', debugMode, '| toggle via: window.mkCartDeliveryResync.debug = true');
    inspectThemeCart();
    attachCheckoutInterceptor();
    attachCartDrawerTriggers();

    var path = window.location && window.location.pathname;
    if (path === '/cart' || path === '/cart/') {
      log('on /cart page → triggering initial run()');
      run('cart-page-load');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
